import React, { useMemo } from 'react';
import { Table, Input, InputNumber, Button } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { FG160, FG161 } from '../../../services/pwg208Api';

const { TextArea } = Input;

interface Tab2DetailsProps {
  details: FG161[];
  formData: FG160;
  isEditing: boolean;
  onAdd: () => void;
  onDelete: (idx: number) => void;
  onUpdate: <K extends keyof FG161>(idx: number, key: K, value: FG161[K]) => void;
  getField: <K extends keyof FG160>(key: K) => FG160[K];
  setField: <K extends keyof FG160>(key: K, value: FG160[K]) => void;
}

const Tab2Details: React.FC<Tab2DetailsProps> = React.memo(({
  details,
  formData,
  isEditing,
  onAdd,
  onDelete,
  onUpdate,
  getField,
  setField,
}) => {
  // Compute summary totals for fg161_19 (本幣不含稅) and fg161_20 (本幣含稅)
  const { totalLocalExTax, totalLocalInTax } = useMemo(() => {
    let exTax = 0;
    let inTax = 0;
    for (const row of details) {
      exTax += row.fg161_19 || 0;
      inTax += row.fg161_20 || 0;
    }
    return { totalLocalExTax: exTax, totalLocalInTax: inTax };
  }, [details]);
  const columns: ColumnsType<FG161> = [
    {
      title: <span data-contract-field="fg161_02">項次</span>,
      dataIndex: 'fg161_02',
      key: 'fg161_02',
      width: 50,
      align: 'center',
    },
    {
      title: <span data-contract-field="fg161_25">S/C-NO</span>,
      dataIndex: 'fg161_25',
      key: 'fg161_25',
      width: 70,
      render: (v: number) => v || '',
    },
    {
      title: <span data-contract-field="fg161_12">產品代號</span>,
      dataIndex: 'fg161_12',
      key: 'fg161_12',
      width: 130,
      render: (v: string, _record: FG161, idx: number) =>
        isEditing ? (
          <Input
            size="small"
            value={v}
            onChange={(e) => onUpdate(idx, 'fg161_12', e.target.value)}
          />
        ) : v,
    },
    {
      title: <span data-contract-field="fg161_11">單位</span>,
      dataIndex: 'fg161_11',
      key: 'fg161_11',
      width: 60,
      render: (v: string, _record: FG161, idx: number) =>
        isEditing ? (
          <Input
            size="small"
            value={v}
            onChange={(e) => onUpdate(idx, 'fg161_11', e.target.value)}
            maxLength={10}
          />
        ) : v,
    },
    {
      title: <span data-contract-field="fg161_13">交運數量</span>,
      dataIndex: 'fg161_13',
      key: 'fg161_13',
      width: 90,
      align: 'right',
      render: (v: number) => v?.toLocaleString() ?? 0,
    },
    {
      title: <span data-contract-field="fg161_14">開票數量</span>,
      dataIndex: 'fg161_14',
      key: 'fg161_14',
      width: 90,
      align: 'right',
      render: (v: number, _record: FG161, idx: number) =>
        isEditing ? (
          <InputNumber
            size="small"
            value={v}
            min={0}
            precision={2}
            onChange={(val) => onUpdate(idx, 'fg161_14', val ?? 0)}
            style={{ width: '100%' }}
          />
        ) : (
          v?.toLocaleString()
        ),
    },
    {
      title: <span data-contract-field="fg161_15">交運單價(CF)</span>,
      dataIndex: 'fg161_15',
      key: 'fg161_15',
      width: 110,
      align: 'right',
      render: (v: number) => v?.toLocaleString() ?? 0,
    },
    {
      title: <span data-contract-field="fg161_22">發票品名</span>,
      dataIndex: 'fg161_22',
      key: 'fg161_22',
      width: 140,
      render: (v: string, _record: FG161, idx: number) =>
        isEditing ? (
          <Input
            size="small"
            value={v}
            onChange={(e) => onUpdate(idx, 'fg161_22', e.target.value)}
            maxLength={60}
          />
        ) : v,
    },
    {
      title: <span data-contract-field="fg161_23">發票單價</span>,
      dataIndex: 'fg161_23',
      key: 'fg161_23',
      width: 100,
      align: 'right',
      render: (v: number, _record: FG161, idx: number) =>
        isEditing ? (
          <InputNumber
            size="small"
            value={v}
            min={0}
            precision={4}
            onChange={(val) => onUpdate(idx, 'fg161_23', val ?? 0)}
            style={{ width: '100%' }}
          />
        ) : (
          v?.toLocaleString()
        ),
    },
    {
      title: <span data-contract-field="fg161_17">不含稅金額</span>,
      dataIndex: 'fg161_17',
      key: 'fg161_17',
      width: 110,
      align: 'right',
      render: (v: number) => v?.toLocaleString() ?? 0,
    },
    {
      title: <span data-contract-field="fg161_18">含稅金額</span>,
      dataIndex: 'fg161_18',
      key: 'fg161_18',
      width: 100,
      align: 'right',
      render: (v: number) => v?.toLocaleString() ?? 0,
    },
    {
      title: <span data-contract-field="fg161_19">本幣不含稅</span>,
      dataIndex: 'fg161_19',
      key: 'fg161_19',
      width: 110,
      align: 'right',
      render: (v: number) => v?.toLocaleString() ?? 0,
    },
    {
      title: <span data-contract-field="fg161_20">本幣含稅</span>,
      dataIndex: 'fg161_20',
      key: 'fg161_20',
      width: 100,
      align: 'right',
      render: (v: number) => v?.toLocaleString() ?? 0,
    },
    {
      title: <span data-contract-field="fg161_21">費用代號</span>,
      dataIndex: 'fg161_21',
      key: 'fg161_21',
      width: 90,
      render: (v: string, _record: FG161, idx: number) =>
        isEditing ? (
          <Input
            size="small"
            value={v}
            onChange={(e) => onUpdate(idx, 'fg161_21', e.target.value)}
            maxLength={8}
          />
        ) : v,
    },
    ...(isEditing
      ? [
          {
            title: '',
            key: 'actions',
            width: 40,
            render: (_: unknown, _record: FG161, idx: number) => (
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => onDelete(idx)}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="pwg208-tab2">
      {isEditing && (
        <div className="tab-toolbar">
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={onAdd}
            type="dashed"
          >
            新增明細
          </Button>
        </div>
      )}
      <Table<FG161>
        columns={columns}
        dataSource={details}
        rowKey={(r) => `${r.fg161_01}-${r.fg161_02}`}
        pagination={false}
        size="small"
        scroll={{ x: 1310, y: 300 }}
        locale={{ emptyText: '無明細資料' }}
      />
      {/* Summary row: totals for key amount fields */}
      <div className="tab-summary">
        共 <strong>{details.length}</strong> 筆明細
      </div>
      <div className="detail-amount-summary" style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
        <div className="form-item" style={{ flex: '0 0 180px' }}>
          <label className="form-label" data-contract-field="fg160_221">開票金額(不含稅)</label>
          <InputNumber
            value={formData.fg160_221}
            disabled
            style={{ width: '100%' }}
            precision={2}
          />
        </div>
        <div className="form-item" style={{ flex: '0 0 180px' }}>
          <label className="form-label" data-contract-field="fg160_231">開票金額(含稅)</label>
          <InputNumber
            value={formData.fg160_231}
            disabled
            style={{ width: '100%' }}
            precision={2}
          />
        </div>
        <div className="form-item" style={{ flex: '0 0 180px' }}>
          <label className="form-label" data-contract-field="fg160_22">本幣不含稅合計</label>
          <InputNumber
            value={totalLocalExTax}
            disabled
            style={{ width: '100%' }}
            precision={2}
          />
        </div>
        <div className="form-item" style={{ flex: '0 0 180px' }}>
          <label className="form-label" data-contract-field="fg160_23">本幣含稅合計</label>
          <InputNumber
            value={totalLocalInTax}
            disabled
            style={{ width: '100%' }}
            precision={2}
          />
        </div>
      </div>

      {/* 備註 (fg160_32) - moved here from Tab1 per FoxPro Page2 layout */}
      <div style={{ marginTop: 12 }}>
        <label className="form-label" data-contract-field="fg160_32">備註</label>
        <TextArea
          value={String(getField('fg160_32'))}
          onChange={(e) => setField('fg160_32', e.target.value)}
          disabled={!isEditing}
          rows={3}
          maxLength={500}
        />
      </div>
    </div>
  );
});

Tab2Details.displayName = 'Tab2Details';

export default Tab2Details;
